import React, { useEffect, useRef, useState } from "react";
import { Download, Share2, Copy, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Canvas-generated shareable card for a winner. 1080x1080 branded image.
 */
export default function ShareFindModal({ winner, open, onClose }) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);
  const caption = `I found the latest Local Loot cash drop in ${winner.city}. It's real 👀💰 #thelocalloot${winner.sponsor ? ` via @${(winner.sponsor.business_name || "").replace(/\s+/g, "")}` : ""}`;

  useEffect(() => {
    if (!open) return;
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const draw = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = 1080, H = 1080;
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    // BG
    ctx.fillStyle = "#0F0F0F";
    ctx.fillRect(0, 0, W, H);

    // Radial glow
    const grad = ctx.createRadialGradient(W * 0.75, H * 0.15, 50, W * 0.75, H * 0.15, 900);
    grad.addColorStop(0, "rgba(118,184,42,0.35)");
    grad.addColorStop(1, "rgba(15,15,15,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // Top band
    ctx.fillStyle = "#E53935";
    ctx.fillRect(0, 0, W, 12);

    // Winner photo or placeholder block
    const imgBoxX = 60, imgBoxY = 60, imgBoxW = W - 120, imgBoxH = 540;
    if (winner.photo_url) {
      try {
        const img = await loadImage(`${process.env.REACT_APP_BACKEND_URL}${winner.photo_url}`);
        // cover crop
        const ratio = Math.max(imgBoxW / img.width, imgBoxH / img.height);
        const nw = img.width * ratio, nh = img.height * ratio;
        ctx.save();
        ctx.beginPath(); ctx.rect(imgBoxX, imgBoxY, imgBoxW, imgBoxH); ctx.clip();
        ctx.drawImage(img, imgBoxX + (imgBoxW - nw) / 2, imgBoxY + (imgBoxH - nh) / 2, nw, nh);
        // dark gradient overlay
        const og = ctx.createLinearGradient(0, imgBoxY, 0, imgBoxY + imgBoxH);
        og.addColorStop(0, "rgba(0,0,0,0.05)");
        og.addColorStop(1, "rgba(0,0,0,0.7)");
        ctx.fillStyle = og;
        ctx.fillRect(imgBoxX, imgBoxY, imgBoxW, imgBoxH);
        ctx.restore();
      } catch {
        drawPhotoPlaceholder(ctx, imgBoxX, imgBoxY, imgBoxW, imgBoxH);
      }
    } else {
      drawPhotoPlaceholder(ctx, imgBoxX, imgBoxY, imgBoxW, imgBoxH);
    }

    // Border
    ctx.strokeStyle = "#76B82A";
    ctx.lineWidth = 6;
    ctx.strokeRect(imgBoxX, imgBoxY, imgBoxW, imgBoxH);

    // Big amount badge — hide the dollar value if admin/winner flagged hide_amount
    const hideAmount = !!winner.hide_amount;
    ctx.fillStyle = "#D7A62A";
    ctx.fillRect(60, 660, hideAmount ? 420 : 320, 110);
    ctx.fillStyle = "#0F0F0F";
    ctx.font = hideAmount ? "900 54px 'Oswald', sans-serif" : "900 68px 'Oswald', sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillText(hideAmount ? "CASH" : `$${winner.amount}`, 80, 716);
    ctx.font = "600 22px 'JetBrains Mono', monospace";
    ctx.fillStyle = "#0F0F0F";
    ctx.fillText("FOUND", 80, 690);

    // User & city
    ctx.fillStyle = "#F5F2E8";
    ctx.font = "900 96px 'Oswald', sans-serif";
    ctx.textBaseline = "alphabetic";
    ctx.fillText(`@${winner.username || winner.name}`.toUpperCase(), 60, 870);
    ctx.fillStyle = "#76B82A";
    ctx.font = "600 36px 'Outfit', sans-serif";
    ctx.fillText(`FOUND IT IN ${(winner.city || "").toUpperCase()}`, 60, 920);

    // Tagline
    ctx.fillStyle = "#F5F2E8";
    ctx.font = "600 26px 'JetBrains Mono', monospace";
    ctx.fillText("THE LOCAL LOOT · REAL CASH · REAL CLUES", 60, 975);
    // Sponsor tag
    if (winner.sponsor) {
      ctx.fillStyle = "#D7A62A";
      ctx.font = "600 22px 'JetBrains Mono', monospace";
      ctx.fillText(`SPONSORED BY ${winner.sponsor.business_name.toUpperCase()}`, 60, 1010);
    }

    // Live dot
    ctx.fillStyle = "#E53935";
    ctx.beginPath(); ctx.arc(W - 90, 150, 18, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#F5F2E8";
    ctx.font = "700 22px 'JetBrains Mono', monospace";
    ctx.textAlign = "right";
    ctx.fillText("WINNER", W - 120, 158);
    ctx.textAlign = "left";

    setDataUrl(canvas.toDataURL("image/png"));
  };

  const loadImage = (src) =>
    new Promise((res, rej) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => res(img);
      img.onerror = rej;
      img.src = src;
    });

  const drawPhotoPlaceholder = (ctx, x, y, w, h) => {
    const g = ctx.createLinearGradient(x, y, x + w, y + h);
    g.addColorStop(0, "#123A1A");
    g.addColorStop(1, "#0F0F0F");
    ctx.fillStyle = g;
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = "#76B82A";
    ctx.font = "900 200px 'Oswald', sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("$", x + w / 2, y + h / 2);
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
  };

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `local-loot-${winner.username || "winner"}.png`;
    a.click();
  };

  const copyCaption = async () => {
    await navigator.clipboard.writeText(caption);
    toast.success("Caption copied");
  };

  const sharePNG = async () => {
    try {
      if (!dataUrl) return;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "local-loot.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption, title: "The Local Loot" });
      } else {
        download();
        copyCaption();
        toast.info("Image downloaded + caption copied — paste into your post.");
      }
    } catch (e) {
      // user cancelled share
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.85)" }}
      data-testid="share-modal"
    >
      <div className="loot-card-flat p-5 max-w-md w-full" style={{ background: "#0F0F0F" }}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Social share</div>
            <h3 className="font-display uppercase text-2xl">Share your find</h3>
          </div>
          <button onClick={onClose} className="text-[#F5F2E8]/60" data-testid="share-close">
            <X size={20} />
          </button>
        </div>

        <div className="border border-[#F5F2E8]/10 overflow-hidden mb-3" style={{ background: "#000" }}>
          <canvas ref={canvasRef} className="w-full h-auto block" />
        </div>

        <textarea
          readOnly
          className="loot-input text-xs"
          rows={3}
          value={caption}
          data-testid="share-caption"
        />

        <div className="mt-3 grid grid-cols-3 gap-2">
          <button onClick={sharePNG} className="btn-loot text-xs" data-testid="share-native">
            <Share2 size={14} className="inline mr-1" /> Share
          </button>
          <button onClick={download} className="btn-ghost text-xs" data-testid="share-download">
            <Download size={14} className="inline mr-1" /> Save PNG
          </button>
          <button onClick={copyCaption} className="btn-ghost text-xs" data-testid="share-copy">
            <Copy size={14} className="inline mr-1" /> Copy text
          </button>
        </div>
        <p className="mt-3 text-[10px] text-[#F5F2E8]/40 font-mono">
          Post on Insta / TikTok with #thelocalloot. Every share helps the hunt grow.
        </p>
      </div>
    </div>
  );
}
